import { useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import type { Job } from "providers/HostedScrapingProvider";
import scraperApi, { JobOrder } from "api";

import Spinner from "components/Spinner";
import Button from "components/Button";
import { SelectOption } from "components/Select";
import { Tooltip } from "components/Tooltip";

import { useHostedScrapingProjects } from "providers/HostedScrapingProvider";
import { HostedScraperCancelJobModal, HostedScraperJobInfoModal } from "components/Modal";
import { LabelledSelect } from "components/LabelledSelect";
import { ensureDate } from "utils/dateUtils";
import { FailureDetail, FailureDetailsModal } from "./FailureDetailsModal";
import { APIErrorList, HIDDEN_TERMINATION_SLUGS } from "./APIErrorList";
import { jobLine } from "./job-list/jobLine";
import { PlaceHolderJobToMarkQueuedJob, shouldShowFailureReportFallback } from "./job-list/utils";
import { JobListSeparator } from "./job-list/JobListSeparator";
import { EmptyJobList } from "./job-list/EmptyJobList";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";


const headerClasses = 'font-droid text-slate-400 text-xs font-medium';

const jobListHeader = () => (
  <>
    <div className={headerClasses}>STATUS</div>
    <div className={headerClasses}>
      <Tooltip content="If your job status says 'error', you can download a status report by clicking on the numbers in the column 'No. of responses'.">NO. OF RESPONSES</Tooltip>
    </div>
    <div className={headerClasses}>STARTED AT</div>
    <div className={headerClasses}>FINISHED AT</div>
    <div className={headerClasses}>COST</div>
    <div className={headerClasses}>DELIVERY</div>
    <div className={headerClasses}>ACTIONS</div>
  </>
);


const jobTerminationReasonFormat = (jobId: number, jobs: Job[] | null): FailureDetail[] | undefined => {
  if (jobs === null) {
    return undefined;
  }
  const job = jobs.find(j => j.id === jobId);
  if (!job || !job.terminationReasonStats) {
    return undefined;
  }

  return Object.keys(job.terminationReasonStats).filter(slug => !HIDDEN_TERMINATION_SLUGS.has(slug)).map<FailureDetail>(slug => {
    const apiErrorObject = APIErrorList[slug];
    if (apiErrorObject === undefined) {
      return {
        slug,
        level: 'info',
        title: 'unknown issue',
        numberOfOccurrences: job.terminationReasonStats?.[slug] ?? 0,
        samples: job.terminationReasonSamples?.[slug] ?? [],
      } as FailureDetail;
    }
    // Display severity: backend high/medium/low → critical/warning/info. The same three
    // levels are also encoded by LEVEL_ORDER + levelConfig in FailureDetailsModal — keep
    // them in sync (worth consolidating if a fourth level is ever added).
    const level =
      apiErrorObject.severity === 'high' ? 'critical'
      : apiErrorObject.severity === 'medium' ? 'warning'
      : 'info'
    return {
      slug,
      level,
      title: apiErrorObject.title,
      description: apiErrorObject.description,
      numberOfOccurrences: job.terminationReasonStats?.[slug] ?? 0,
      samples: job.terminationReasonSamples?.[slug] ?? [],
      // v1 renders guidance links only (docs + support). Action CTAs are not shown yet.
      docsLink: apiErrorObject.docsLink,
      supportLink: apiErrorObject.supportLink,
    } as FailureDetail;
  });
  // Order isn't significant here — FailureDetailsModal groups by LEVEL_ORDER.
};

const sortByOptions: SelectOption<JobOrder>[] = [
  { name: "Job completed", value: "job_completed_first" },
  { name: "Job failed", value: "job_failed_first" },
  { name: "Last run first", value: "last_run_first" },
  { name: "Last run last", value: "last_run_last" },
];

type IJobListProps = {
  projectId: number;
  supposedToRunAt?: Date;
  projectShouldRunSoon: boolean;
  scheduleNow: () => void;
}

export function JobList({projectId, supposedToRunAt, projectShouldRunSoon, scheduleNow}: IJobListProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeSubscription } = useActiveSubscription();
  const { runningProjectInfo } = useHostedScrapingProjects();
  const { jobs, jobsLoading: inProgress, jobOrder, setJobOrder, numberOfJobs, setNumberOfJobs, fetchCurrentProject, currentProject } = useHostedScrapingProjects();
  const [ progressDetailsJobId, setProgressDetailsJobId ] = useState<number | null>(null);
  const [ activeCancelJobModalJobId, setActiveCancelJobModalJobId ] = useState<number | null>(null);
  const [ queuedPlaceHolderCancelClicked, setQueuedPlaceHolderCancelClicked ] = useState(false);
  // Snapshotted so a background jobs refetch can't change the open modal.
  // showCTA uses index === 0 as "latest job", which only holds under the default sort. Follow-up: backend isLatest flag.
  const [ failureDetails, setFailureDetails ] = useState<{ details: FailureDetail[]; showCTA: boolean } | null>(null);

  const onProgressDetailsClicked = useCallback((jobId: number) => {
    setProgressDetailsJobId(jobId);
  }, [setProgressDetailsJobId]);

  const onViewUrlsClicked = useCallback((jobId: number) => {
    navigate(`/projects/${projectId}/jobs/${jobId}`);
  }, [navigate, projectId]);

  const onCancelJobClicked = useCallback((jobId: number) => {
    setActiveCancelJobModalJobId(jobId);
  }, []);

  const onQueuedPlaceholderCancelClicked = useCallback(async () => {
    setQueuedPlaceHolderCancelClicked(true);
    try {
      await scraperApi.hostedScraping.postponeJob(projectId);
      await fetchCurrentProject(projectId);
    } finally {
      setQueuedPlaceHolderCancelClicked(false);
    }
  }, [projectId, fetchCurrentProject]);

  const onViewIssuesClicked = useCallback((jobId: number, index: number) => {
    const details = jobTerminationReasonFormat(jobId, jobs);
    if (details && details.length > 0) {
      setFailureDetails({ details, showCTA: index === 0 });
      return;
    }
    // Without structured reasons there is nothing to break down, so send the user to the
    // existing error-report view, which fetches and renders the job's failure report.
    const job = jobs?.find(j => j.id === jobId);
    if (job && shouldShowFailureReportFallback(job)) {
      navigate(`/datapipeline/project/${job.projectId}/error-report/${job.publicJobId}`, { state: { backgroundLocation: location } });
    }
  }, [jobs, navigate, location]);

  const jobOrderSelectOption = useMemo(() => {
    return sortByOptions.find(opt => opt.value === jobOrder);
  }, [ jobOrder ]);
  const jobListElements = jobs !== null ? jobs.flatMap(jobLine(projectId, runningProjectInfo, activeSubscription, onProgressDetailsClicked, onCancelJobClicked, onViewIssuesClicked, onViewUrlsClicked)) : [];
  jobListElements.pop(); // remove last separator
  // Add a queued placeholder
  const supposedToRunAtDate = ensureDate(supposedToRunAt);
  const neededToAddQueuedPlaceholder =
    // The job is due
    supposedToRunAtDate && (supposedToRunAtDate.getTime() < Date.now())
    // The placeholder job is not dismissed yet
    && !queuedPlaceHolderCancelClicked
    // There are no running jobs (there can be a short period when a job the job list is updated but the is project not yet)
    && !(jobs ?? []).some(job => job.status === 'running');

  if (neededToAddQueuedPlaceholder) {
    jobListElements.unshift(<JobListSeparator key={`separator-queued-placeholder`} />);
    const queuedPlaceholder = { id: -1, status: 'queued' } as PlaceHolderJobToMarkQueuedJob;
    const queuedPlaceholderElements = jobLine(projectId, runningProjectInfo, activeSubscription, onProgressDetailsClicked, onQueuedPlaceholderCancelClicked, () => {})(queuedPlaceholder, -1);
    jobListElements.unshift(...queuedPlaceholderElements.slice(0, -1)); // all except the trailing separator
  }

  const showEmptyJobList = jobs !== null && jobs.length === 0 && (!Boolean(currentProject?.supposedToRunAt) || currentProject?.supposedToRunAt! < new Date());

  return (
    <>
      { failureDetails !== null
        && <FailureDetailsModal details={failureDetails.details} showCTA={failureDetails.showCTA} projectShouldRunSoon={projectShouldRunSoon} rerunCallback={scheduleNow} onClose={() => setFailureDetails(null)}/>
      }

      <div className="w-full border border-slate-200 p-2 bg-white">
        { progressDetailsJobId !== null && (
          <HostedScraperJobInfoModal jobList={jobs} jobId={progressDetailsJobId} onClose={() => setProgressDetailsJobId(null)} />
        )}
        { activeCancelJobModalJobId !== null && (
          <HostedScraperCancelJobModal jobId={activeCancelJobModalJobId} projectId={projectId} onClose={() => setActiveCancelJobModalJobId(null)} />
        )}
        <div className="m-4 flex flex-row items-center justify-between text-gray dark:text-neutral-600">

          <div className="flex flex-row items-center">
            Jobs
            { inProgress && <div className="ml-2"><Spinner className="w-3 h-3 animate-spin"/></div> }
          </div>
          <LabelledSelect
            label="Sort by"
            value={jobOrderSelectOption || { name: "Please select", value: undefined } }
            options={sortByOptions}
            onChange={ (newValue: SelectOption<JobOrder>) => setJobOrder(newValue.value) }
            className="w-[216px]"
          />
        </div>


        { showEmptyJobList && <EmptyJobList firstJobScheduled={supposedToRunAt}/> }
        { jobs !== null && !showEmptyJobList && (
          <div className="overflow-x-auto w-full">
            <div className="grid grid-cols-7 px-6 w-full min-[768px]:min-w-[1136px]">
                {jobListHeader()}
                  {jobListElements}

              { jobs.length >= numberOfJobs &&
                (<div className="flex flex-col items-center col-span-7">
                  <Button text="Load more" className="button button-secondary" onClick={() => setNumberOfJobs(numberOfJobs + 10)} size="MD" />
                </div>)
              }
            </div>
          </div>
        )}
      </div>
    </>
  );
}
